// Ordering and filtering a scan's rows for the mode pages.
//
// It lives outside the components because vitest here has no React renderer:
// anything left inside a component cannot be tested at all, and which row comes
// first is exactly the kind of thing that is wrong quietly.
import type { Mode, OfferRow, PackageLot } from "../api/types";
import { bestFirst, byPrice, byText } from "./compare";

export type OfferSort = "item" | "trader" | "price";
export type LotSort = "trader" | "total";

export function sortOffers(rows: OfferRow[], sort: OfferSort, mode: Mode): OfferRow[] {
  const direction = bestFirst(mode);
  // A copy, because Array.prototype.sort is in place and these rows come
  // straight out of react-query's cache. Sort is stable in ES2019+, which is
  // what keeps equal rows from swapping between renders.
  return [...rows].sort((a, b) => {
    if (sort === "trader") return byText(a.nick, b.nick) || byText(a.item, b.item);
    if (sort === "price") return byPrice(a.price, b.price, direction);
    return byText(a.item, b.item) || byPrice(a.price, b.price, direction);
  });
}

export function sortLots(lots: PackageLot[], sort: LotSort): PackageLot[] {
  return [...lots].sort((a, b) => {
    if (sort === "total") return byPrice(a.total, b.total, -1);
    return byText(a.nick, b.nick);
  });
}

export function filterLots(lots: PackageLot[], needle: string): PackageLot[] {
  const term = needle.trim().toLowerCase();
  if (!term) return lots;
  return lots.filter((lot) => lot.items.some((i) => i.name.toLowerCase().includes(term)));
}

/** One item's whole presence in one mode of one scan. */
export interface ItemAggregate {
  item_id: number;
  item: string;
  /** The mode's best price, or null when no row of it had a readable one. */
  best: number | null;
  traders: number;
  offers: number;
}

export type AggSort = "item" | "best" | "traders";

/** Collapse a mode's offers to one row per item, the way the index's table
 *  reads — the same item under six traders is six rows here and one there.
 *
 *  Grouped by `item_id` rather than by name: the name is what OCR read and the
 *  id is what the server resolved it to, so two spellings of one item collapse
 *  into the one row they are. That is what carrying the id is for.
 *
 *  An item whose every price failed to read keeps its row with a null best. It
 *  was on offer — the scan saw it — and dropping it would hide that, while
 *  calling its best 0 would put a failed read at the top of a sell page. */
export function aggregateOffers(rows: OfferRow[], mode: Mode): ItemAggregate[] {
  const wantsMax = mode === "buy";
  const byItem = new Map<number, ItemAggregate & { nicks: Set<string> }>();

  for (const row of rows) {
    let agg = byItem.get(row.item_id);
    if (!agg) {
      agg = {
        item_id: row.item_id, item: row.item, best: null,
        traders: 0, offers: 0, nicks: new Set<string>(),
      };
      byItem.set(row.item_id, agg);
    }
    agg.offers += 1;
    agg.nicks.add(row.nick.toLowerCase());
    if (row.price !== null) {
      agg.best = agg.best === null
        ? row.price
        : (wantsMax ? Math.max(agg.best, row.price) : Math.min(agg.best, row.price));
    }
  }

  return [...byItem.values()].map(({ nicks, ...agg }) => ({ ...agg, traders: nicks.size }));
}

export function sortAggregates(
  aggs: ItemAggregate[], sort: AggSort, mode: Mode,
): ItemAggregate[] {
  const direction = bestFirst(mode);
  return [...aggs].sort((a, b) => {
    // Busiest first: a count answers "how many", and the interesting end of
    // that question is the big one.
    if (sort === "traders") return b.traders - a.traders || byText(a.item, b.item);
    if (sort === "best") return byPrice(a.best, b.best, direction) || byText(a.item, b.item);
    return byText(a.item, b.item);
  });
}

export function filterAggregates(aggs: ItemAggregate[], needle: string): ItemAggregate[] {
  const term = needle.trim().toLowerCase();
  if (!term) return aggs;
  return aggs.filter((agg) => agg.item.toLowerCase().includes(term));
}
